'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { sendGTMEvent } from '@next/third-parties/google';
import { ReCaptchaProvider, useReCaptcha } from 'next-recaptcha-v3';
import { usePathname, useRouter } from 'next/navigation';
import { type ReactNode, useCallback, useEffect, useId, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useServerAction } from 'zsa-react';

import { env } from '@natu/env';
import { Button } from '@natu/ui/Button';
import {
  Checkbox,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Textarea,
} from '@natu/ui/Form';
import { Loader } from '@natu/ui/Loader';
import { Typography } from '@natu/ui/Typography';
import { type PredefinedGAEvents } from '@natu/utils/events';
import { getEventPayload, type EventProps } from '@natu/utils/getEventPayload';
import { usePageContext } from '@natu/utils/PageContextProvider';

import {
  competitorReportRequestRetryableErrorMessage,
  workEmailRequiredMessage,
} from './competitorReportRequestFormMessages';
import {
  competitorReportRequestFormSchema,
  type CompetitorReportRequestFormValues,
} from './competitorReportRequestFormSchema';
import { sendCompetitorReportRequestAction } from './sendCompetitorReportRequestAction';
import { validateCompetitorReportRequestAction } from './validateCompetitorReportRequestAction';

interface CompetitorReportRequestFormContentProps {
  event_form_name?: string;
  event_internal_campaign_name?: string;
  formTitle?: ReactNode;
  introCopy?: ReactNode;
  processingConsent: ReactNode;
  reCaptchaKey: string;
  sendEvent?: boolean;
  submitLabel: string;
  successDestination?: string;
}

type CompetitorReportRequestField = keyof CompetitorReportRequestFormValues;
type CompetitorReportRequestLifecycleEvent = 'start' | 'abandon' | 'error' | 'success';

export const CompetitorReportRequestFormContent = ({
  reCaptchaKey,
  ...formProps
}: CompetitorReportRequestFormContentProps) => (
  <ReCaptchaProvider reCaptchaKey={reCaptchaKey}>
    <CompetitorReportRequestForm {...formProps} />
  </ReCaptchaProvider>
);

const CompetitorReportRequestForm = ({
  event_form_name,
  event_internal_campaign_name,
  formTitle,
  introCopy,
  processingConsent,
  sendEvent,
  submitLabel,
  successDestination,
}: Omit<CompetitorReportRequestFormContentProps, 'reCaptchaKey'>) => {
  const [isSuccessful, setIsSuccessful] = useState(false);
  const [submissionError, setSubmissionError] = useState<string>();
  const abandonmentSent = useRef(false);
  const currentField = useRef<CompetitorReportRequestField | undefined>(undefined);
  const formStarted = useRef(false);
  const requestAccepted = useRef(false);
  const successHeadingId = useId();
  const successMessageId = useId();
  const successStateRef = useRef<HTMLDivElement>(null);
  const submissionInFlight = useRef(false);
  const pathname = usePathname();
  const { push } = useRouter();
  const { isCampaign } = usePageContext() || {};
  const analyticsEnabled = sendEvent !== false;
  const form = useForm<CompetitorReportRequestFormValues>({
    defaultValues: {
      email: '',
      companyName: '',
      competitorWebsites: '',
      processingConsent: false,
    },
    resolver: zodResolver(competitorReportRequestFormSchema),
    mode: 'onBlur',
    shouldFocusError: true,
  });

  const sendLifecycleEvent = useCallback(
    (type: CompetitorReportRequestLifecycleEvent, fieldName?: CompetitorReportRequestField) => {
      if (!analyticsEnabled) return;

      const eventDefinitions: Record<
        CompetitorReportRequestLifecycleEvent,
        { event: PredefinedGAEvents; eventProps: EventProps }
      > = {
        start: {
          event: 'MK_form_start',
          eventProps: { event_form_name },
        },
        abandon: {
          event: 'MK_form_abandoned',
          eventProps: { event_field_name: fieldName, event_form_name },
        },
        error: {
          event: 'MK_form_error',
          eventProps: { event_error_type: 'request_failed', event_form_name },
        },
        success: {
          event: 'K_form_sent',
          eventProps: { event_form_name, event_internal_campaign_name },
        },
      };
      const definition = eventDefinitions[type];

      try {
        const eventPayload = getEventPayload({
          event: definition.event,
          eventProps: definition.eventProps,
          isCampaign,
          pathname,
        });

        sendGTMEvent({ event: definition.event, ...eventPayload });
      } catch {
        // Analytics must never affect request submission or the success transition.
      }
    },
    [analyticsEnabled, event_form_name, event_internal_campaign_name, isCampaign, pathname],
  );

  const sendAbandonment = useCallback(() => {
    if (
      !analyticsEnabled ||
      !formStarted.current ||
      requestAccepted.current ||
      abandonmentSent.current
    ) {
      return;
    }

    abandonmentSent.current = true;
    sendLifecycleEvent('abandon', currentField.current);
  }, [analyticsEnabled, sendLifecycleEvent]);

  useEffect(() => {
    if (isSuccessful) successStateRef.current?.focus();
  }, [isSuccessful]);

  useEffect(() => {
    if (!analyticsEnabled) return;

    const subscription = form.watch((_values, { name }) => {
      if (!name || requestAccepted.current) return;

      currentField.current = name;

      if (formStarted.current) return;

      formStarted.current = true;
      sendLifecycleEvent('start');
    });

    return () => subscription.unsubscribe();
  }, [analyticsEnabled, form, sendLifecycleEvent]);

  useEffect(() => {
    if (!analyticsEnabled) return;

    const handlePageExit = () => sendAbandonment();

    window.addEventListener('beforeunload', handlePageExit);
    window.addEventListener('pagehide', handlePageExit);

    return () => {
      window.removeEventListener('beforeunload', handlePageExit);
      window.removeEventListener('pagehide', handlePageExit);
      sendAbandonment();
    };
  }, [analyticsEnabled, sendAbandonment]);

  const handleActionError = (error: {
    code: 'business_email_required' | 'request_failed' | 'validation_error';
    field?: 'email';
    message: string;
  }) => {
    if (error.code === 'business_email_required' && error.field === 'email') {
      setSubmissionError(undefined);
      form.setError(
        'email',
        { message: workEmailRequiredMessage, type: 'server' },
        { shouldFocus: true },
      );

      return;
    }

    if (error.code === 'validation_error' && error.field === 'email') {
      setSubmissionError(undefined);
      form.setError('email', { message: error.message, type: 'server' }, { shouldFocus: true });

      return;
    }

    setSubmissionError(competitorReportRequestRetryableErrorMessage);
  };

  const { execute: validateCompetitorReportRequest, isPending: isRequestValidationPending } =
    useServerAction(validateCompetitorReportRequestAction, {
      onError: ({ err }) => {
        handleActionError(err);
        sendLifecycleEvent('error');
      },
    });

  const { execute: sendCompetitorReportRequest, isPending: isRequestPending } = useServerAction(
    sendCompetitorReportRequestAction,
    {
      onError: ({ err }) => {
        handleActionError(err);
        sendLifecycleEvent('error');
      },
      onSuccess: () => {
        setSubmissionError(undefined);
        requestAccepted.current = true;
        formStarted.current = false;
        sendLifecycleEvent('success');

        if (!successDestination) {
          setIsSuccessful(true);

          return;
        }

        try {
          push(successDestination);
        } catch {
          setIsSuccessful(true);
        }
      },
    },
  );
  const { executeRecaptcha } = useReCaptcha();
  const isSubmitting =
    form.formState.isSubmitting || isRequestValidationPending || isRequestPending;
  const isInteractionDisabled = isSubmitting || requestAccepted.current;

  const onSubmit = async (values: CompetitorReportRequestFormValues) => {
    if (submissionInFlight.current || requestAccepted.current) return;

    submissionInFlight.current = true;
    setSubmissionError(undefined);

    try {
      const applicationOrigin = new URL(env.NEXT_PUBLIC_APP_URL).origin;
      const sourceUrl = new URL(pathname || '/', applicationOrigin).toString();
      const [requestValidation] = await validateCompetitorReportRequest({ ...values, sourceUrl });

      if (!requestValidation) return;

      const gRecaptchaToken = await executeRecaptcha('competitor_report_request_submit');

      if (!gRecaptchaToken) {
        setSubmissionError(competitorReportRequestRetryableErrorMessage);

        return;
      }

      await sendCompetitorReportRequest({ ...values, gRecaptchaToken, sourceUrl });
    } catch {
      setSubmissionError(competitorReportRequestRetryableErrorMessage);
    } finally {
      submissionInFlight.current = false;
    }
  };

  if (isSuccessful) {
    return (
      <div
        aria-describedby={successMessageId}
        aria-labelledby={successHeadingId}
        className="focus:ring-primary-600 rounded-2xl bg-white px-6 py-8 text-center shadow-2xl focus:outline-none focus:ring-2 focus:ring-offset-2 xl:px-10 xl:py-14"
        ref={successStateRef}
        role="status"
        tabIndex={-1}
      >
        <Typography
          className="mb-6 font-semibold"
          component="h2"
          id={successHeadingId}
          variant="display-md-34"
        >
          Thank you
        </Typography>
        <Typography className="font-medium text-gray-700" id={successMessageId}>
          We&apos;ll get in touch to go over a few details and confirm scope. In the meantime, the
          sample report is yours to keep.
        </Typography>
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-white px-6 py-8 shadow-2xl xl:px-10 xl:py-14">
      {formTitle}
      {introCopy}
      <Form {...form}>
        <form aria-busy={isSubmitting} noValidate onSubmit={form.handleSubmit(onSubmit)}>
          <div className="mb-8 flex flex-col gap-y-5">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-gray-700">Work email</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      autoComplete="email"
                      className="shadow-xs border-gray-300"
                      maxLength={254}
                      required
                      type="email"
                    />
                  </FormControl>
                  <FormMessage role="alert" />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="companyName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-gray-700">Company name</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      autoComplete="organization"
                      className="shadow-xs border-gray-300"
                      maxLength={200}
                      required
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="competitorWebsites"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-gray-700">
                    Competitor websites <span className="text-gray-500">(optional)</span>
                  </FormLabel>
                  <FormControl>
                    <Textarea {...field} className="min-h-32" maxLength={4000} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="processingConsent"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-start gap-x-3">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        className="mt-0.5"
                        name={field.name}
                        onBlur={field.onBlur}
                        onCheckedChange={checked => field.onChange(checked === true)}
                        ref={field.ref}
                        required
                      />
                    </FormControl>
                    <FormLabel className="cursor-pointer text-xs font-normal leading-[1.3] text-gray-600 [&_.prose]:text-xs [&_.prose]:leading-[1.3] [&_div]:inline [&_p]:inline">
                      {processingConsent}
                    </FormLabel>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {submissionError && (
            <p className="text-destructive mb-6 text-sm font-medium" role="alert">
              {submissionError}
            </p>
          )}

          <Button
            className="flex min-w-48 items-center gap-x-2 text-[0.813rem] font-semibold uppercase leading-[1.5rem]"
            disabled={isInteractionDisabled}
            type="submit"
            variant="primary"
          >
            {isSubmitting ? 'Sending request…' : submitLabel}
            {isSubmitting && <Loader />}
          </Button>
        </form>
      </Form>
    </div>
  );
};
